/**
 * Signer — rozhranie pre podpisovanie eventov (F4AI-18, S12).
 *
 * Dve implementácie za JEDNÝM rozhraním, podľa zadania:
 *  - **NIP-07** (`window.nostr`): ak prehliadač má extension (Alby, nos2x…),
 *    použijeme `getPublicKey()` + `signEvent()` — súkromný kľúč nikdy
 *    neopustí extension. `created_at`/id počítame my, extension doplní
 *    `id`+`sig`; výsledok overujeme `verifyEvent` z nostr-tools, aby
 *    rozhranie nemohlo prekvapiť polovičným eventom.
 *  - **nsec z `localStorage`** (`forum4ai_nsec`, S11): `finalizeEvent`
 *    z `nostr-tools/pure` — čistá synchronná funkcia, žiadna sieť.
 *
 * `resolveSigner()` je jediné miesto, ktoré sa rozhoduje (NIP-07 má
 * prednosť, inak nsec). Testy mocknú obe strany: NIP-07 falošným
 * `window.nostr`, nsec deterministickým `TEST_NSEC`.
 */
import { finalizeEvent, getEventHash, getPublicKey, verifyEvent } from 'nostr-tools/pure';

import type { NostrEvent, NostrTag } from '@forum4ai/shared';

/** šablóna bez `id`/`sig`/`pubkey` — to, čo dodáva volajúci (Composer) */
export interface EventTemplate {
  kind: number;
  content: string;
  tags: NostrTag[] | string[][];
  created_at: number;
}

/** podpisovateľ cez ktorékoľvek pozadie — to, čo ujíma Composer */
export interface Signer {
  readonly type: 'nip07' | 'nsec';
  /** 64-hex x-only pubkey podpisovateľa */
  readonly pubkey: string;
  /** podepíše šablónu a vráti kánonických 7 polí (POST /events teliesko) */
  sign(template: EventTemplate): Promise<NostrEvent>;
}

// ── NIP-07 ────────────────────────────────────────────────────────────────

/**
 * NIP-07 provider (`window.nostr`) — len to, čo skutočne voláme.
 * `signEvent` dostane event bez `id`/`sig` (niektoré implementácie
 * vyžadujú aj `pubkey`, preto ho posielame) a vráti ho doplnené o
 * `id`+`sig` — nie všetky vracajú aj `pubkey`.
 */
export interface Nip07Provider {
  getPublicKey(): Promise<string>;
  signEvent(event: Record<string, unknown>): Promise<Record<string, unknown>>;
}

/** nájde `window.nostr`, ak v prostredí existuje a vyzerá byť použiteľný */
export function getNip07Provider(): Nip07Provider | null {
  const scope = globalThis as { window?: { nostr?: Partial<Nip07Provider> } };
  const nostr = scope.window?.nostr;
  if (
    nostr !== undefined &&
    nostr !== null &&
    typeof nostr.getPublicKey === 'function' &&
    typeof nostr.signEvent === 'function'
  ) {
    return nostr as Nip07Provider;
  }
  return null;
}

const HEX_64 = /^[0-9a-f]{64}$/;
const HEX_128 = /^[0-9a-f]{128}$/;

/**
 * Signer cez NIP-07 extension. `getPublicKey()` voláme raz pri vytvorení —
 * pubkey potrebujeme pred podpisom (id hash ho obsahuje).
 */
export async function createNip07Signer(provider: Nip07Provider): Promise<Signer> {
  const pubkey = await provider.getPublicKey();
  if (!HEX_64.test(pubkey)) {
    throw new Error(`NIP-07 getPublicKey vrátil neplatný pubkey: ${pubkey.slice(0, 16)}…`);
  }

  async function sign(template: EventTemplate): Promise<NostrEvent> {
    const unsigned = {
      pubkey,
      created_at: template.created_at,
      kind: template.kind,
      tags: template.tags as string[][],
      content: template.content,
    };
    const expectedId = getEventHash(unsigned);
    const signed = await provider.signEvent({ ...unsigned });

    const returnedId = typeof signed['id'] === 'string' ? (signed['id'] as string) : expectedId;
    if (returnedId !== expectedId) {
      throw new Error(
        `NIP-07 signEvent vrátil id ${returnedId.slice(0, 12)}…, ale očakávali sme ${expectedId.slice(0, 12)}… (rozdny created_at/tags?)`,
      );
    }
    const sig = typeof signed['sig'] === 'string' ? (signed['sig'] as string) : '';
    if (!HEX_128.test(sig)) {
      throw new Error('NIP-07 signEvent nevrátil 128-hex podpis');
    }
    const returnedPubkey = typeof signed['pubkey'] === 'string' ? (signed['pubkey'] as string) : pubkey;
    if (returnedPubkey !== pubkey) {
      throw new Error('NIP-07 signEvent vrátil iný pubkey, ako sľúbil getPublicKey');
    }

    const event: NostrEvent = {
      id: expectedId,
      pubkey,
      created_at: template.created_at,
      kind: template.kind,
      tags: template.tags as NostrTag[],
      content: template.content,
      sig,
    };
    if (!verifyEvent(event as unknown as Parameters<typeof verifyEvent>[0])) {
      throw new Error('NIP-07 podpis neprešiel verifyEvent (podpis alebo id nesedí s obsahom)');
    }
    return event;
  }

  return { type: 'nip07', pubkey, sign };
}

// ── nsec (localStorage) ───────────────────────────────────────────────────

/** Signer cez `finalizeEvent` so secret key (S11 cesta, `forum4ai_nsec`). */
export function createNsecSigner(secretKey: Uint8Array): Signer {
  const pubkey = getPublicKey(secretKey);
  function sign(template: EventTemplate): Promise<NostrEvent> {
    const signed = finalizeEvent(
      {
        kind: template.kind,
        content: template.content,
        tags: template.tags as string[][],
        created_at: template.created_at,
      },
      secretKey,
    );
    const event: NostrEvent = {
      id: signed.id,
      pubkey: signed.pubkey,
      created_at: signed.created_at,
      kind: signed.kind,
      tags: signed.tags as NostrTag[],
      content: signed.content,
      sig: signed.sig,
    };
    if (getEventHash({ ...signed }) !== event.id) {
      throw new Error('interná chyba: id eventu nezodpovedá hashu obsahu');
    }
    return Promise.resolve(event);
  }
  return { type: 'nsec', pubkey, sign };
}

// ── voľba signera ─────────────────────────────────────────────────────────

export interface ResolveSignerOptions {
  /** explicitný provider (testy); default: live `window.nostr` */
  nip07?: Nip07Provider | null;
  /** secret key pre nsec fallback (identity zo S11); null = zakázaný */
  secretKey?: Uint8Array | null;
}

/**
 * Vyber signera: NIP-07 má prednosť (kľúč mimo stránky), inak nsec zo
 * správy identity. Bez oboch → chyba s vetou, ktorú UI ukáže používáčovi.
 */
export async function resolveSigner(options: ResolveSignerOptions = {}): Promise<Signer> {
  const provider = options.nip07 !== undefined ? options.nip07 : getNip07Provider();
  if (provider !== null && provider !== undefined) {
    return createNip07Signer(provider);
  }
  const secretKey = options.secretKey ?? null;
  if (secretKey !== null) {
    return createNsecSigner(secretKey);
  }
  throw new Error('žiaden signer: window.nostr nie je dostupné a chýba nsec identita');
}
